import React, { useState, useCallback } from "react";
import SubPageLayout from "../../../components/shared/SubPageLayout";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

const CompanyStructure = () => {
  const breadcrumbItems = [
    { label: "Administrative", link: "/administrative" },
    { label: "Company Structure", link: "/administrative/company-structure" },
  ];

  // State for selected node and detail panel
  const [selectedNode, setSelectedNode] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");

  // Mock data for organizational structure
  const [orgStructure, setOrgStructure] = useState({
    id: 1,
    type: "department",
    name: "Executive Leadership",
    children: [
      {
        id: 2,
        type: "department",
        name: "Investment Management",
        children: [
          {
            id: 3,
            type: "team",
            name: "Portfolio Management",
            children: [
              {
                id: 4,
                type: "employee",
                name: "Sarah Johnson",
                title: "Senior Portfolio Manager",
                email: "sarah.j@example.com",
                phone: "(555) 123-4567",
              },
              {
                id: 5,
                type: "employee",
                name: "Michael Chen",
                title: "Portfolio Analyst",
                email: "michael.c@example.com",
                phone: "(555) 123-4568",
              },
            ],
          },
          {
            id: 6,
            type: "team",
            name: "Research & Analysis",
            children: [
              {
                id: 7,
                type: "employee",
                name: "Emily Rodriguez",
                title: "Research Director",
                email: "emily.r@example.com",
                phone: "(555) 123-4569",
              },
            ],
          },
        ],
      },
      {
        id: 8,
        type: "department",
        name: "Client Services",
        children: [
          {
            id: 9,
            type: "team",
            name: "Account Management",
            children: [
              {
                id: 10,
                type: "employee",
                name: "David Wilson",
                title: "Senior Account Manager",
                email: "david.w@example.com",
                phone: "(555) 123-4570",
              },
            ],
          },
          {
            id: 11,
            type: "team",
            name: "Client Support",
            children: [
              {
                id: 12,
                type: "employee",
                name: "Lisa Thompson",
                title: "Client Support Lead",
                email: "lisa.t@example.com",
                phone: "(555) 123-4571",
              },
            ],
          },
        ],
      },
      {
        id: 13,
        type: "department",
        name: "Operations",
        children: [
          {
            id: 14,
            type: "team",
            name: "Technology",
            children: [
              {
                id: 15,
                type: "employee",
                name: "James Lee",
                title: "IT Director",
                email: "james.l@example.com",
                phone: "(555) 123-4572",
              },
            ],
          },
          {
            id: 16,
            type: "team",
            name: "Compliance",
            children: [
              {
                id: 17,
                type: "employee",
                name: "Rachel Martinez",
                title: "Compliance Officer",
                email: "rachel.m@example.com",
                phone: "(555) 123-4573",
              },
            ],
          },
        ],
      },
    ],
  });

  // Handler for node selection
  const handleNodeSelect = (node) => {
    setSelectedNode(node);
    setShowDetailPanel(true);
  };

  // Handler for adding new node
  const handleAddNode = () => {
    // Implementation for adding new node
  };

  // Handler for node drag and drop
  const handleNodeMove = useCallback((draggedId, targetId) => {
    // Implementation for node movement
  }, []);

  // Render toolbar actions
  const renderToolbar = () => (
    <div className="flex items-center justify-between mb-6 bg-surface-default dark:bg-surface-default p-4 rounded-lg shadow">
      <div className="flex items-center space-x-4 flex-1">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search departments, teams, or employees..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <div className="absolute left-3 top-2.5 text-gray-400">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
        <select
          className="px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="all">All</option>
          <option value="department">Departments</option>
          <option value="team">Teams</option>
          <option value="employee">Employees</option>
        </select>
      </div>
      <div className="flex items-center space-x-4">
        <button
          onClick={handleAddNode}
          className="px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-[#018529] transition-colors"
        >
          Add New
        </button>
        <button className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100">
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
        </button>
        <button className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100">
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </button>
      </div>
    </div>
  );

  // Render organizational chart
  const renderNode = (node) => {
    const isSelected = selectedNode?.id === node.id;
    return (
      <div className="flex flex-col items-center">
        <div
          key={node.id}
          className={`relative w-64 p-4 border rounded-lg mb-4 ${
            isSelected
              ? "border-blue-500 ring-2 ring-blue-500"
              : "border-gray-200 dark:border-gray-700"
          } ${node.type === "department" ? "bg-blue-50" : node.type === "team" ? "bg-green-50" : "bg-surface-default dark:bg-surface-default"}`}
        >
          <div
            className="cursor-pointer"
            onClick={() => handleNodeSelect(node)}
            role="button"
            tabIndex={0}
          >
            <div className="font-medium text-gray-900 dark:text-gray-100 text-center">
              {node.name}
            </div>
            {node.title && (
              <div className="text-sm text-gray-500 dark:text-gray-500 text-center">
                {node.title}
              </div>
            )}
          </div>
        </div>
        {node.children && node.children.length > 0 && (
          <>
            <div className="w-px h-8 bg-gray-300"></div>
            <div className="relative flex">
              <div className="absolute top-0 left-1/2 w-full -translate-x-1/2 h-px bg-gray-300"></div>
              <div className="flex space-x-12">
                {node.children.map((child, index) => (
                  <div key={child.id} className="relative">
                    {index > 0 && (
                      <div className="absolute top-0 left-0 w-full h-px bg-gray-300"></div>
                    )}
                    {renderNode(child)}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // Render detail panel
  const renderDetailPanel = () => {
    if (!selectedNode) return null;

    return (
      <div
        className="fixed right-0 top-0 h-screen w-96 bg-surface-default dark:bg-surface-default shadow-lg dark:shadow-none transform transition-transform duration-300 ease-in-out"
        style={{
          transform: showDetailPanel ? "translateX(0)" : "translateX(100%)",
        }}
      >
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {selectedNode.name}
              </h2>
              {selectedNode.title && (
                <p className="text-sm text-gray-500 dark:text-gray-500">{selectedNode.title}</p>
              )}
            </div>
            <button
              onClick={() => setShowDetailPanel(false)}
              className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            {selectedNode.type === "employee" && (
              <>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">
                    Contact Information
                  </h3>
                  <div className="mt-2 space-y-2">
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {selectedNode.email}
                    </p>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {selectedNode.phone}
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">
                    Role Details
                  </h3>
                  <div className="mt-2 space-y-2">
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      Department: Investment Management
                    </p>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      Reports to: Director of Investments
                    </p>
                  </div>
                </div>
              </>
            )}

            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">Actions</h3>
              <div className="mt-2 space-y-2">
                <button className="w-full px-4 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100">
                  Edit Details
                </button>
                <button className="w-full px-4 py-2 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100">
                  Remove
                </button>
                {selectedNode.type !== "employee" && (
                  <button className="w-full px-4 py-2 text-sm text-green-600 bg-green-50 rounded-lg hover:bg-green-100">
                    Add Sub-Role
                  </button>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">
                Recent Activity
              </h3>
              <div className="mt-2 space-y-2">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <p>Role updated - 2 days ago</p>
                  <p>Department changed - 1 week ago</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <SubPageLayout
        title="Company Structure"
        subtitle="Manage your organization's hierarchy and departments"
        breadcrumbItems={breadcrumbItems}
      >
        <div className="relative min-h-0 flex-1">
          {renderToolbar()}
          <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-12 overflow-x-auto">
            <div className="min-w-max">{renderNode(orgStructure)}</div>
          </div>
          {renderDetailPanel()}
        </div>
      </SubPageLayout>
    </DndProvider>
  );
};

export default CompanyStructure;
